import React, { useEffect, useState } from "react";
import { TasticProps } from "frontastic/tastics/types";
import Breadcrumb from "components/commercetools-ui/atoms/breadcrumb";
import Link from "components/commercetools-ui/atoms/link";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { HomeIcon } from "@heroicons/react/24/outline";
import usePath from "helpers/hooks/usePath";
import Image from "frontastic/lib/image";
import Button from "components/commercetools-ui/atoms/button";
import Typography from "components/commercetools-ui/atoms/typography";
import Markdown from "components/commercetools-ui/organisms/markdown";
import { VIDEO_PLAYED } from "helpers/constants/events";

const ContentstackVideoGrid = ({ data }: TasticProps<any>) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const { path } = usePath();

 // Inside your component
useEffect(() => {
  if (videoUrl) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }

  // Clean-up function ensures no lingering effects on component unmount
  return () => {
    document.body.style.overflow = "auto";
  };
}, [videoUrl]);

  const generateBreadcrumbs = () => {
    const pathSegments = path.split("/").filter(Boolean);
    const pathSegmentsWithoutLast = pathSegments.slice(0, -1);

    return [
      <span
        key="static-media"
        className="text-12 font-normal capitalize leading-4 text-primary-black"
        style={{ cursor: "default" }}
      >
        Media
      </span>,
      ...pathSegmentsWithoutLast.map((segment, index) => {
        const href = `/${pathSegmentsWithoutLast.slice(0, index + 1).join("/")}/`;
        const isProductOrBrandSegment =
          segment.includes("products") || segment.includes("brands") || segment.includes("solutions");

        return (
          <span key={segment}>
            {isProductOrBrandSegment ? (
              <span
                className="text-12 font-normal capitalize leading-4 text-primary-black"
                style={{ cursor: "default" }}
              >
                {segment.replace(/-/g, " ")}
              </span>
            ) : (
              <Link link={href}>
                <span className="text-12 font-normal capitalize leading-4 text-[#666]">
                  {segment.replace(/-/g, " ")}
                </span>
              </Link>
            )}
          </span>
        );
      }),
    ];
  };

  const sections = data?.data?.dataSource?.sections || [];
  const infoSections = sections
    .filter((section: any) => section.info_section_with_image)
    .map((section: any) => section.info_section_with_image);

  const imageTextSections = sections
    .filter((section: any) => section.section_with_image_text)
    .map((section: any) => section.section_with_image_text);

  const openVideoModal = (url: string) => {
    if (url.includes("youtube.com/watch")) {
      const embedUrl = url.replace("watch?v=", "embed/") + "?autoplay=1";
      setVideoUrl(embedUrl);
    } else {
      console.error("Invalid YouTube URL:", url);
    }
  };

  const closeVideoModal = () => setVideoUrl(null);
  const handleVideo = (url: any) => {
    openVideoModal(url);
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: VIDEO_PLAYED,
      video: url,
    });
  };
  const hasContent = infoSections.length > 0 || imageTextSections.length > 0;
  if (!hasContent) {
    return <p>No sections to display</p>;
  }

  return (
    <div className="sections-container">
      {/* Breadcrumb */}
      <div className="hidden px-24 pb-8 pt-16 md:!block md:px-28 lg:px-48 xl:px-60 2xl:pl-60 2xl:pr-80 3xl:pl-80">
        <Breadcrumb Separator={<ChevronRightIcon className="w-16" />} listClassName="!gap-x-16">
          <Link link={"/"}>
            <HomeIcon className="mt-6 w-20" />
          </Link>
          {generateBreadcrumbs()}
        </Breadcrumb>
      </div>

      {/* Video Modal */}
      {videoUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeVideoModal}
        >
          <div
            className="relative bg-white w-11/12 md:w-3/4 lg:w-1/2 p-4 rounded-lg shadow-lg"
            onClick={(e) => e.stopPropagation()} // Prevent close on modal click
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={closeVideoModal}
            >
              ✕
            </button>
            <iframe
              width="100%"
              className="h-[315px] lg:h-[315px] xl:h-[450px]"
              src={videoUrl}
              title="Video Player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

 {/* Render info_section_with_image */}
 {infoSections.length > 0 ? (
        infoSections.map((section: any, index: number) => {
          const {
            title,
            description,
            image,
            desktop_image_alignment = "Left",
            mobile_image_alignment = "Top",
            additional_css = "",
            ctas = [],
          } = section;

          const desktopAlignmentClass =
            desktop_image_alignment === "Left"
              ? "lg:flex-row"
              : desktop_image_alignment === "Right"
              ? "lg:flex-row-reverse"
              : desktop_image_alignment === "Center"
              ? "lg:justify-center"
              : "";

          const mobileAlignmentClass =
            mobile_image_alignment === "Top"
              ? "flex-col"
              : mobile_image_alignment === "Bottom"
              ? "flex-col-reverse"
              : mobile_image_alignment === "Center"
              ? "justify-center flex-col"
              : "";

          return (
            <div
              key={index}
              className={`${additional_css} flex items-center pb-24 gap-24 lg:gap-0 ${desktopAlignmentClass} ${mobileAlignmentClass}`}
            >
              <div className="relative w-full flex-1 flex-grow sm:px-24 lg:px-48 xl:px-60 3xl:px-80">
                {image?.url && (
                  <Image
                    loading="lazy"
                    src={image.url}
                    alt={image.description || "Section Image"}
                    className="w-full"
                    style={{
                      height: "auto",
                      maxHeight: "auto",
                      objectFit: "cover",
                    }}
                  />
                )}
              </div>
              <div className="flex-1 flex-grow px-[20.5px] sm:px-24 lg:px-48 xl:px-60 3xl:px-80">
                <div>
                  {title && (
                    <Markdown
                      markdown={title}
                      className="relative text-32 font-medium leading-10 pb-10"
                    />
                  )}
                  {description && (
                    <Markdown
                      markdown={description}
                      className="list-container pt-10 text-16 font-normal leading-6 text-[#333] md:text-18 lg:leading-8 2xl:text-20 [&>p]:mb-16"
                    />
                  )}
                  {ctas.map((cta: any, idx: number) => (
                    <div className="flex justify-center lg:justify-start mt-[50px]" key={idx}>
                      <Link
                        link={cta.button?.href || "#"}
                        target={cta.button?.href?.includes("https://") ? "_blank" : ""}
                        rel="noopener noreferrer"
                      >
                        <Button
                          style={{
                            backgroundColor: cta.button_background_color?.hex,
                            border: `1px solid ${cta.button_border_color?.hex}`,
                            color: cta.button_text_color?.hex,
                            cursor: "pointer !important",
                            padding: "10px 28px",
                            borderRadius: "0",
                          }}
                        >
                          <Typography as="span" style={{ fontWeight: 600, fontStyle: "italic", fontSize: "16px" }}>
                            {cta.button?.title}
                          </Typography>
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="py-12"></div> /* Add space if no infoSections */
      )}

      {/* Render info_section_with_image (4-column layout) */}
      {infoSections.map((section: any, index: number) => {
        const { title, content_with_icon: contentItems, additional_css } = section;

        return (
          <div
            key={index}
            className={`video-grid w-full items-center justify-between mt-20 mb-40 px-[20.5px] sm:px-24 lg:px-48 xl:px-60 3xl:px-80 ${
              additional_css || ""
            }`}
          >
             {/* <h2 className="text-2xl font-bold mb-6">{title}</h2> */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {contentItems?.map((item: any, idx: number) => {
                const {
                  icon = { url: "" },
                  link: videoUrl,
                  title: itemTitle,
                  content,
                } = item;

                const { url: thumbnailUrl } = icon;

                return (
                  <div key={idx} className="content-item flex flex-col relative hover:bg-[#F3EDE9] transition duration-300 group">
                    {thumbnailUrl && (
                      <div className="relative">
                        <img
                          src={thumbnailUrl}
                          alt="Thumbnail"
                          className="thumbnail-image object-cover"
                        />
                        {videoUrl && (
                          <button
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white"
                            onClick={() => handleVideo(videoUrl)}
                          >
                            ▶
                          </button>
                        )}
                      </div>
                    )}
                    {itemTitle && (
                  <div className=" ">
                  <Markdown
                    markdown={itemTitle}
                    className="text-lg font-bold p-2"
                  />
                </div>
                
                  )}
                    {content && (
                      <div
                        className="content p-2"
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    )}
                     {/* Add arrow icon */}
                 {/*    <div className="mt-2 text-right p-2 transform transition-transform duration-300 group-hover:translate-x-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="w-32 h-32 text-gray-600"
                >
                  <path
                    fillRule="evenodd"
                    d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 01.083 1.32l-.083.094-6 6a1 1 0 01-1.497-1.32l.083-.094L17.585 12H4a1 1 0 01-.117-1.993L4 10h13.585l-4.293-4.293a1 1 0 01-.083-1.32z"
                    clipRule="evenodd"
                  />
                </svg>
              </div> */}
              </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Render section_with_image_text */}
      {imageTextSections.map((section: any, index: number) => {
        const { title, description, image, additional_css } = section;

        return (
          <div key={index} className={`image-text-section ${additional_css || ""}`}>
            <div className="flex flex-col sm:flex-row items-center">
              {image?.url && (
                <img
                  src={image.url}
                  alt={image.description || "Section Image"}
                  className="section-image w-full sm:w-1/2"
                />
              )}
              <div className="text-content sm:ml-6 mt-4 sm:mt-0">
                {title && (
                  <div
                    className="text-xl font-bold"
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                )}
                {description && (
                  <div
                    className="description mt-2"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContentstackVideoGrid;
